// Sky: the sun and moon crossing the top of the board, and the night tint over the map.
// Everything is driven by a fractional hour so motion is continuous between sim ticks.
import { Container, Graphics } from 'pixi.js';
import { SUNRISE, SUNSET } from '../sim';
import { palette } from './palette';

export interface Sky {
  /** 1 at full day, 0 at deep night; eases through twilight. */
  dayness: number;
  /** 0..1 progress of the sun across its arc, or undefined at night. */
  sunP: number | undefined;
  /** 0..1 progress of the moon across its arc, or undefined by day. */
  moonP: number | undefined;
}

const DAY_LEN = SUNSET - SUNRISE;
const NIGHT_LEN = 24 - DAY_LEN;

export function skyState(hour: number): Sky {
  const h = ((hour % 24) + 24) % 24;
  const day = h >= SUNRISE && h < SUNSET;
  const sunP = day ? (h - SUNRISE) / DAY_LEN : undefined;
  const sinceSunset = h >= SUNSET ? h - SUNSET : h + (24 - SUNSET);
  const moonP = day ? undefined : sinceSunset / NIGHT_LEN;
  // Signed altitude: +1 at noon, -1 at midnight, 0 at the horizon.
  const alt = day ? Math.sin(Math.PI * (sunP ?? 0)) : -Math.sin(Math.PI * (moonP ?? 0));
  // Twilight is short and smooth: full day above 0.25, full night below -0.12.
  const dayness = smoothstep(-0.12, 0.25, alt);
  return { dayness, sunP, moonP };
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

export interface SunLayer {
  /** Map coordinates: sun and moon discs. Sits above the tint. */
  arc: Container;
  /** Map coordinates: multiply tint over the terrain only. */
  tint: Graphics;
  update(hour: number): Sky;
}

const NIGHT = { r: 0x74, g: 0x80, b: 0xaa };
const DUSK = { r: 0xf5, g: 0xe3, b: 0xd6 };

export function createSunLayer(mapSize: { w: number; h: number }, margin: number): SunLayer {
  const arc = new Container();
  arc.eventMode = 'none';

  const sun = new Graphics();
  sun.circle(0, 0, 30).fill({ color: palette.sun, alpha: 0.08 });
  sun.circle(0, 0, 20).fill({ color: palette.sun, alpha: 0.16 });
  sun.circle(0, 0, 11).fill({ color: palette.sun });
  arc.addChild(sun);

  const moon = new Graphics();
  moon.circle(0, 0, 22).fill({ color: 0xdfe6f5, alpha: 0.1 });
  moon.circle(0, 0, 9).fill({ color: 0xeef2fb });
  moon.circle(3.6, -2.2, 7.6).cut();
  arc.addChild(moon);

  const tint = new Graphics();
  tint.eventMode = 'none';
  tint.blendMode = 'multiply';
  const mx = mapSize.w * margin;
  const my = mapSize.h * margin;
  let lastDayness = -1;

  const place = (g: Graphics, p: number): void => {
    g.position.set(mapSize.w * (0.06 + 0.88 * p), 44 - Math.sin(Math.PI * p) * 30);
  };

  return {
    arc,
    tint,
    update(hour) {
      const sky = skyState(hour);
      if (sky.sunP !== undefined) {
        sun.visible = true;
        place(sun, sky.sunP);
        // Fade in and out across the horizon rather than popping.
        sun.alpha = smoothstep(0, 0.08, Math.sin(Math.PI * sky.sunP));
      } else sun.visible = false;
      if (sky.moonP !== undefined) {
        moon.visible = true;
        place(moon, sky.moonP);
        moon.alpha = smoothstep(0, 0.08, Math.sin(Math.PI * sky.moonP)) * 0.95;
      } else moon.visible = false;

      if (Math.abs(sky.dayness - lastDayness) > 0.004) {
        lastDayness = sky.dayness;
        // White (no effect) -> warm dusk -> blue night, so twilight reads as twilight.
        const d = sky.dayness;
        const c =
          d > 0.5
            ? mix({ r: 255, g: 255, b: 255 }, DUSK, (1 - d) * 2)
            : mix(DUSK, NIGHT, 1 - d * 2);
        tint.clear();
        tint
          .rect(-mx, -my, mapSize.w + 2 * mx, mapSize.h + 2 * my)
          .fill({ color: (c.r << 16) | (c.g << 8) | c.b });
      }
      return sky;
    },
  };
}

function mix(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
  t: number,
) {
  const k = Math.max(0, Math.min(1, t));
  return {
    r: Math.round(a.r + (b.r - a.r) * k),
    g: Math.round(a.g + (b.g - a.g) * k),
    b: Math.round(a.b + (b.b - a.b) * k),
  };
}
